import {
  IEmployeeBillingSummaryRow,
  IEquipmentBillingSummaryRow,
  IProductionQuantityBillingSummaryRow,
  ITableConfigEntity,
} from '../../interfaces/internal-interfaces';
import { IBillingSummaryRow, ISharepointDisplayData } from '../BillingTimesheet';
import { EmployeeCatsRecord } from '../employeeCatsRecord';
import { EquipmentCatsRecord } from '../EquipmentCatsRecord';
import { ProductionEquipmentCatsRecord } from '../productionEquipmentCatsRecord';
import { ProductionQuantityCatsRecord } from '../ProductionQuantityCatsRecords';
import { ProductionText } from '../ProductionText';
import { ProductionTextRecord } from '../ProductionTextRecord';
import { makeComplexConfigItem, makeDaysConfig, makeTextDaysConfig } from './table-config-helpers';

export const BillingOverviewConfig: ITableConfigEntity<keyof IBillingSummaryRow>[] = [
  makeComplexConfigItem<IBillingSummaryRow>('Name', 'description', 'left-text', 5, false, false, {
    headerClass: 'text-left',
    class: 'h-full',
  }),
  makeComplexConfigItem<IBillingSummaryRow>('Billed As', 'Zmatnr', 'left-text', 3, false, false, {
    headerClass: 'text-left',
    class: 'h-full',
  }),
  makeComplexConfigItem<IBillingSummaryRow>('Amount', 'total', 'text', 1, false, false, {
    headerClass: 'text-right',
    class: '!text-right !justify-end h-full',
  }),
];

export const billingEmployeeWeeklyConfig: ITableConfigEntity<keyof IEmployeeBillingSummaryRow>[] = [
  {
    header: 'Employee',
    type: 'left-text',
    key: 'employee',
    shouldSpanAllRows: true,
    columns: 10,
    isReadOnly: true,
    headerClass: 'text-left pl-[5px]',
    class: 'h-full',
  },
  {
    header: 'Billed As',
    type: 'left-text',
    key: 'billCode',
    columns: 8,
    shouldSpanAllRows: false,
    isReadOnly: true,
    headerClass: 'text-left pl-[5px]',
    shouldDetectClick: true,
    clickValue: 'billcodeChange',
  },
  ...makeDaysConfig('number-box', 2),
  {
    header: 'Total',
    key: 'total',
    type: 'text',
    shouldSpanAllRows: false,
    shouldTotal: true,
    columns: 3,
    isReadOnly: true,
    class: 'h-full',
  },
];

export function makeBillingEquipmentWeeklyConfig(isReadonly: boolean): ITableConfigEntity<keyof IEquipmentBillingSummaryRow>[] {
  return [
    makeComplexConfigItem<IEquipmentBillingSummaryRow>('Equipment', 'equipment', 'left-text', 5, true, true, {
      shouldDetectClick: true,
      clickValue: 'horizontalBulkEntry',
      headerClass: 'text-left pl-[5px]',
      class: 'h-full',
    }),
    makeComplexConfigItem<IEquipmentBillingSummaryRow>('Billed As', 'billCode', 'left-text', 4, false, true, {
      shouldDetectClick: !isReadonly,
      clickValue: 'billcodeChange',
      class: 'h-full',
      headerClass: 'text-left pl-[5px]',
    }),
    ...makeDaysConfig('number-box', 1),
    makeComplexConfigItem<IEquipmentBillingSummaryRow>('Total', 'total', 'text', 2, false, true, {
      shouldTotal: true,
      class: 'h-full',
    }),
  ];
}

export function makeBillingProductionQuantityWeeklyConfig(
  isReadonly: boolean,
): ITableConfigEntity<keyof IProductionQuantityBillingSummaryRow>[] {
  return [
    makeComplexConfigItem<IProductionQuantityBillingSummaryRow>('Description', 'display', 'left-text', 4, true, true, {
      shouldDetectClick: true,
      clickValue: 'horizontalBulkEntry',
      headerClass: 'text-left pl-[5px]',
      class: 'h-full',
    }),
    makeComplexConfigItem<IProductionQuantityBillingSummaryRow>('Billed As', 'billCode', 'left-text', 4, false, isReadonly, {
      shouldDetectClick: !isReadonly,
      clickValue: 'billcodeChange',
      headerClass: 'text-left pl-[5px]',
      class: 'h-full',
    }),
    ...makeDaysConfig('number-box', 1),
    makeComplexConfigItem<IProductionQuantityBillingSummaryRow>('Total', 'total', 'text', 2, false, true, { class: 'h-full' }),
  ];
}

export function makeBillingAttachmentConfig(): ITableConfigEntity<keyof ISharepointDisplayData>[] {
  return [
    makeComplexConfigItem<ISharepointDisplayData>('Timesheet', 'timesheetNo', 'left-text', 1, false, false, {
      headerClass: '!text-left self-end',
      class: 'h-full',
    }),
    makeComplexConfigItem('File Name', 'displayName', 'text-input', 4, false, false, {
      class: 'ml-2',
      headerClass: '!text-left pl-2 self-end',
      minTextRows: 1,
      shouldDetectEnter: true,
    }),
    makeComplexConfigItem<ISharepointDisplayData>('File Ext', 'fileType', 'left-text', 1, false, false, {
      headerClass: '!text-left self-end',
      class: 'h-full',
    }),
    makeComplexConfigItem<ISharepointDisplayData>('Date', 'weekendingDate', 'text', 1, false, true, {
      headerClass: 'self-end',
      class: 'h-full',
    }),
    makeComplexConfigItem<ISharepointDisplayData>('Uploaded', 'hasBeenUploaded', 'value-icon', 1, false, false, {
      class: 'text-[#96c955]',
      icon: 'check',
      shouldDetectClick: true,
      headerClass: 'self-end',
    }),
    makeComplexConfigItem<ISharepointDisplayData>('View', 'canView', 'value-disabled-icon', 1, false, false, {
      headerClass: 'self-end',
      icon: 'description',
      shouldDetectClick: true,
      clickValue: 'visitSharepointUrl',
    }),
    makeComplexConfigItem<ISharepointDisplayData>('Delete', 'canDelete', 'value-disabled-icon', 1, false, false, {
      headerClass: 'self-end',
      icon: 'backspace',
      shouldDetectClick: true,
      clickValue: 'deleteSharepointFile',
    }),
  ];
}

export const billingProductionTextWeeklyConfig = [
  makeComplexConfigItem<ProductionText>('Production Item', 'display', 'bold-text', 1, false, true, { class: '!text-black' }),
  ...makeTextDaysConfig('embedded-text'),
];

export function makeBillingProductionTextDailyConfig(): ITableConfigEntity[] {
  return [
    makeComplexConfigItem<ProductionTextRecord>('Value', 'LongTextstr', 'text-input', 11, false, false, {
      minTextRows: 1,
      maxTextRows: 2,
      shouldRemoveMatStyling: false,
      headerClass: '!text-left',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'post_add',
      shouldDetectClick: true,
      clickValue: 'addClicked',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      svgIcon: 'move-time',
      shouldDetectClick: true,
      clickValue: 'moveTimeSheetClicked',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'delete',
      shouldDetectClick: true,
      clickValue: 'deleteClicked',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'info',
      shouldDetectClick: true,
      clickValue: 'showCats',
    }),
  ];
}

export function makeBillingEmployeeDailyConfig(): ITableConfigEntity[] {
  return [
    makeComplexConfigItem<EmployeeCatsRecord>('Paid As', 'activityCodeDescription', 'left-text', 8, false, true, {
      headerClass: '!text-left',
      class: 'mt-2 !items-start',
    }),
    makeComplexConfigItem<EmployeeCatsRecord>('Hours', 'Catshours', 'text', 2, false, false, { class: 'mt-2 !items-start' }),
    makeComplexConfigItem('', 'split', 'icon', 2, false, true, {
      icon: 'account_tree',
      shouldDetectClick: true,
      clickValue: 'splitClicked',
    }),
    makeComplexConfigItem<EmployeeCatsRecord>('Billed As', 'Zmatnr', 'select', 8, false, false, { headerClass: '!text-left' }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 2, false, false, {
      svgIcon: 'move-time',
      shouldDetectClick: true,
      clickValue: 'moveTimeSheetClicked',
    }),
    // makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {icon: 'save', detectClick: true, clickValue: 'defaultClicked'}),
    makeComplexConfigItem('', 'enableIcons', 'icon', 2, false, false, {
      icon: 'info',
      shouldDetectClick: true,
      clickValue: 'showCats',
    }),
  ];
}

export function makeBillingEquipmentDailyConfig(): ITableConfigEntity[] {
  return [
    makeComplexConfigItem<EquipmentCatsRecord>('Billed As', 'Zmatnr', 'select', 5, false, false, { headerClass: '!text-left' }),
    makeComplexConfigItem<EquipmentCatsRecord>('Hours', 'Catshours', 'hours', 3, false, false),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'post_add',
      shouldDetectClick: true,
      clickValue: 'addClicked',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      svgIcon: 'move-time',
      shouldDetectClick: true,
      clickValue: 'moveTimeSheetClicked',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'delete',
      shouldDetectClick: true,
      clickValue: 'deleteClicked',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'info',
      shouldDetectClick: true,
      clickValue: 'showCats',
    }),
  ];
}

export function makeBillingProductionQuantityAndProductionEquipmentDailyConfig(isReadonly: boolean): ITableConfigEntity[] {
  return [
    makeComplexConfigItem<ProductionQuantityCatsRecord & ProductionEquipmentCatsRecord>(
      'Billed As',
      'Zmatnr',
      isReadonly ? 'text' : 'select',
      5,
      false,
      false,
      { headerClass: '!text-left', class: isReadonly ? '!mt-4 !justify-start !items-start' : '' },
    ),
    makeComplexConfigItem<ProductionQuantityCatsRecord & ProductionEquipmentCatsRecord>(
      'Quantity',
      'Quantity',
      'input',
      2,
      false,
      false,
    ),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'post_add',
      shouldDetectClick: true,
      clickValue: 'addClicked',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      svgIcon: 'move-time',
      shouldDetectClick: true,
      clickValue: 'moveTimeSheetClicked',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'delete',
      shouldDetectClick: true,
      clickValue: 'deleteClicked',
    }),
    makeComplexConfigItem('', 'enableIcons', 'icon', 1, false, false, {
      icon: 'info',
      shouldDetectClick: true,
      clickValue: 'showCats',
    }),
  ];
}
